/**
 * Eén gedeelde modellenlijst voor foto's en het modelfilter (04-10-2026, Jordy).
 *
 * Jordy wil overal waar modellen staan de profielfoto zien, en op elke pagina
 * met modellen kunnen filteren op één model. Daarvoor is per pagina dezelfde
 * kennis nodig: welke profielen (slug/id) er zijn, met welke foto, en welke
 * profielen bij hetzelfde model horen (FC + F2F).
 *
 * Eén ophaalactie van `/api/admin/models/personas?split=true` (één rij per
 * profiel), gedeeld door alle pagina's en onderdelen in deze sessie en vijf
 * minuten bewaard. Voor een chatter levert de server al alleen zijn eigen
 * modellen, dus ook alleen hun foto's.
 *
 * Een "model" in het filter is een persoon: de profielen met dezelfde
 * artiestennaam, net als bij Create/Edit chatter (ModelKeuze). Rozy is dus één
 * keuze, die zowel f2f_rozy als haar FC-profiel dekt.
 */
import { useEffect, useMemo, useState } from 'react'
import { api } from './api'

export interface Profiel {
  id: number
  slug: string
  name: string
  stage_name: string | null
  avatar_url: string | null
  active: boolean
  fancentro_model_id?: string | null
  f2f_model_id?: string | null
}

export interface Model {
  /** Sleutel van de persoon: artiestennaam in kleine letters. */
  sleutel: string
  naam: string
  avatar: string | null
  ids: number[]
  slugs: string[]
  fc: boolean
  f2f: boolean
}

const BEWAAR_MS = 5 * 60_000
let cache: { ts: number; belofte: Promise<Profiel[]> } | null = null

/** 07-10-2026 (Jordy): na verbergen of weer tonen meteen opnieuw ophalen,
 *  zodat de modelbalk en filters het direct volgen. */
export function vergeetModellen(): void {
  cache = null
}

function haalProfielen(): Promise<Profiel[]> {
  if (cache && Date.now() - cache.ts < BEWAAR_MS) return cache.belofte
  const belofte = api.get<Profiel[]>('/api/admin/models/personas?split=true')
    .catch((e) => { cache = null; throw e })
  cache = { ts: Date.now(), belofte }
  return belofte
}

function naamVan(p: Profiel): string {
  return (p.stage_name || p.name || p.slug).trim()
}

function isF2f(p: Profiel): boolean {
  return Boolean(p.f2f_model_id) || p.slug.startsWith('f2f_')
}

export function groepeer(profielen: Profiel[]): Model[] {
  const per = new Map<string, Model>()
  for (const p of profielen) {
    if (p.slug.startsWith('nas_')) continue // mapregels van de NAS, geen model
    const naam = naamVan(p)
    const sleutel = naam.toLowerCase()
    const m = per.get(sleutel) ?? { sleutel, naam, avatar: null, ids: [], slugs: [], fc: false, f2f: false }
    m.ids.push(p.id)
    m.slugs.push(p.slug.toLowerCase())
    if (!m.avatar && p.avatar_url) m.avatar = p.avatar_url
    if (isF2f(p)) m.f2f = true
    else m.fc = true
    per.set(sleutel, m)
  }
  return [...per.values()].sort((a, b) => a.naam.localeCompare(b.naam))
}

export interface Modellen {
  laden: boolean
  profielen: Profiel[]
  modellen: Model[]
  /** Profiel bij een slug, id of (artiesten)naam; hoofdletters tellen niet. */
  zoek: (sleutel: string | number | null | undefined) => Profiel | undefined
  /** Het model (persoon) waar dit profiel bij hoort. */
  modelVan: (sleutel: string | number | null | undefined) => Model | undefined
}

export function useModellen(): Modellen {
  const [profielen, setProfielen] = useState<Profiel[] | null>(null)
  useEffect(() => {
    let levend = true
    haalProfielen().then((p) => { if (levend) setProfielen(p) }).catch(() => { if (levend) setProfielen([]) })
    return () => { levend = false }
  }, [])
  return useMemo(() => {
    const lijst = profielen ?? []
    const perSlug = new Map(lijst.map((p) => [p.slug.toLowerCase(), p]))
    const perId = new Map(lijst.map((p) => [p.id, p]))
    const perNaam = new Map<string, Profiel>()
    for (const p of lijst) {
      const n = naamVan(p).toLowerCase()
      // Bij dezelfde naam liever een profiel met foto.
      if (!perNaam.has(n) || (!perNaam.get(n)!.avatar_url && p.avatar_url)) perNaam.set(n, p)
    }
    const modellen = groepeer(lijst)
    const perModel = new Map<string, Model>()
    for (const m of modellen) for (const s of m.slugs) perModel.set(s, m)
    const zoek = (s: string | number | null | undefined): Profiel | undefined => {
      if (s == null || s === '') return undefined
      if (typeof s === 'number') return perId.get(s)
      const k = String(s).toLowerCase()
      return perSlug.get(k) ?? perNaam.get(k) ?? (/^\d+$/.test(k) ? perId.get(Number(k)) : undefined)
    }
    const modelVan = (s: string | number | null | undefined): Model | undefined => {
      const p = zoek(s)
      return p ? perModel.get(p.slug.toLowerCase()) : undefined
    }
    return { laden: profielen === null, profielen: lijst, modellen, zoek, modelVan }
  }, [profielen])
}
